<template>
    <div class="energy-dashboard">
      <!-- Capacity Bid Card -->
      <CapacityBidCard 
        :capacity-bid="latestCapacityBid" 
        @update="updateCapacityBid" 
      />
      
      <!-- Card containing suppliers table -->
      <Card class="mt-4">
        <template #title>
          <div class="flex align-items-center">
            <i class="pi pi-list mr-2 text-primary"></i>
            Liste des Fournisseurs d'Énergie
          </div>
        </template>
        <template #subtitle>
          <div class="text-500">Gérez vos fournisseurs d'énergie</div>
        </template>
        <template #content>
          <!-- Toolbar -->
          <div class="table-header flex justify-content-between align-items-center mb-3">
            <div>
              <Button icon="pi pi-plus" label="Ajouter un fournisseur" @click="openNewSupplierDialog"></Button>
            </div>
            <div class="flex align-items-center">
              <span class="p-input-icon-left">
                <i class="pi pi-search"></i>
                <InputText v-model="filters.global" placeholder="Rechercher..." />
              </span>
            </div>
          </div>
          
          <!-- Suppliers Table -->
          <DataTable 
            :value="suppliers"
            :paginator="true"
            :rows="10"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            v-model:filters="filters"
            filterDisplay="menu"
            responsiveLayout="scroll"
            :loading="loading"
            stripedRows
            class="p-datatable-sm"
          >
            <Column field="active" header="Actif" :exportable="false" style="min-width: 100px">
              <template #body="slotProps">
                <InputSwitch v-model="slotProps.data.active" @change="toggleActive(slotProps.data.id, slotProps.data.active)" />
              </template>
            </Column>
            
            <Column field="name" header="Fournisseur" sortable style="min-width: 150px">
              <template #body="slotProps">
                <span class="font-medium">{{ slotProps.data.name }}</span>
              </template>
            </Column>
            
            <Column field="elec" header="ELEC" sortable style="min-width: 80px">
              <template #body="slotProps">
                <i class="pi" :class="{ 'pi-check-circle text-green-500': slotProps.data.elec, 'pi-times-circle text-red-500': !slotProps.data.elec }"></i>
              </template>
            </Column>
            
            <Column field="gaz" header="GAZ" sortable style="min-width: 80px">
              <template #body="slotProps">
                <i class="pi" :class="{ 'pi-check-circle text-green-500': slotProps.data.gaz, 'pi-times-circle text-red-500': !slotProps.data.gaz }"></i>
              </template>
            </Column>
            
            <Column field="segment_elec" header="SEGMENT ELEC" sortable style="min-width: 150px"></Column>
            <Column field="segment_gaz" header="SEGMENT GAZ" sortable style="min-width: 150px"></Column>
            <Column field="marge" header="MARGE" sortable style="min-width: 120px"></Column>
            <Column field="restriction" header="Restriction" sortable style="min-width: 150px"></Column>
            <Column field="credit_note" header="CREDIT NOTE" sortable style="min-width: 150px"></Column>
            <Column field="turpe" header="TURPE" sortable style="min-width: 120px"></Column>
            <Column field="capa" header="CAPA" sortable style="min-width: 120px"></Column>
            <Column field="cee" header="CEE" sortable style="min-width: 120px"></Column>
            <Column field="energie_verte" header="Energie VERTE" sortable style="min-width: 150px"></Column>
            <Column field="commentaire" header="Commentaire" style="min-width: 200px"></Column>
            
            <Column :exportable="false" style="min-width: 100px">
              <template #body="slotProps">
                <div class="flex gap-2">
                  <Button icon="pi pi-pencil" outlined rounded @click="editSupplier(slotProps.data)"></Button>
                  <Button icon="pi pi-trash" outlined rounded severity="danger" @click="confirmDeleteSupplier(slotProps.data)"></Button>
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>
      
      <!-- Supplier Form Dialog -->
      <SupplierForm
        v-model:visible="supplierDialog"
        :supplier="supplier"
        :edit-mode="editMode"
        @save="saveSupplier"
        @cancel="closeSupplierDialog"
      />
      
      <!-- Delete Confirmation -->
      <Dialog v-model:visible="deleteSupplierDialog" header="Confirmation" :style="{ width: '450px' }" :modal="true">
        <div class="flex align-items-center justify-content-center">
          <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem"></i>
          <span v-if="supplier">Êtes-vous sûr de vouloir supprimer <b>{{ supplier.name }}</b>?</span>
        </div>
        <template #footer>
          <Button label="Non" icon="pi pi-times" outlined @click="deleteSupplierDialog = false"></Button>
          <Button label="Oui" icon="pi pi-check" severity="danger" @click="deleteSupplier"></Button>
        </template>
      </Dialog>
    </div>
  </template>
  
  <script setup>
  import { ref, reactive, onMounted } from 'vue'
  import { useToast } from 'primevue/usetoast'
  //import { useConfirm } from 'primevue/useconfirm'
  import { FilterMatchMode } from 'primevue/api'
  import CapacityBidCard from './CapacityBidCard.vue'
  import SupplierForm from './SupplierForm.vue'
  //import axios from 'axios'
  import api from '@/service/api'

  const toast = useToast()
  
  // Data
  const suppliers = ref([])
  const latestCapacityBid = ref(0)
  const loading = ref(false)
  
  // UI State
  const supplierDialog = ref(false)
  const deleteSupplierDialog = ref(false)
  const editMode = ref(false)
  
  // Empty supplier template
  const emptySupplier = {
    name: '',
    active: true,
    elec: false,
    gaz: false,
    segment_elec: '',
    segment_gaz: '',
    marge: '',
    restriction: '',
    credit_note: '',
    turpe: '',
    capa: '',
    cee: '',
    energie_verte: '',
    commentaire: ''
  }
  
  // Current supplier
  const supplier = ref({ ...emptySupplier })
  
  // Table filters
  const filters = reactive({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  })
  
  // Load data on component mount
  onMounted(async () => {
    await fetchSuppliers()
    await fetchLatestCapacityBid()
  })
  
  // Fetch suppliers from API
  const fetchSuppliers = async () => {
    loading.value = true
    try {
      const response = await api.get('/suppliers/')
      suppliers.value = response.data
    } catch (error) {
      console.error('Error fetching suppliers:', error)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de charger les fournisseurs',
        life: 3000
      })
    } finally {
      loading.value = false
    }
  }
  
  // Fetch latest capacity bid from API
  const fetchLatestCapacityBid = async () => {
    try {
      const response = await api.get('/latest-capacity-bid/')
      latestCapacityBid.value = response.data.amount
    } catch (error) {
      console.error('Error fetching capacity bid:', error)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de charger l\'enchère de capacité',
        life: 3000
      })
    }
  }
  
  // Update capacity bid
  const updateCapacityBid = async (amount) => {
    try {
      await api.post('/capacity-bids/', { amount })
      latestCapacityBid.value = amount
      toast.add({
        severity: 'success',
        summary: 'Succès',
        detail: 'Enchère de capacité mise à jour',
        life: 3000
      })
    } catch (error) {
      console.error('Error updating capacity bid:', error)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Échec de la mise à jour',
        life: 3000
      })
    }
  }
  
  // Toggle supplier active status
  const toggleActive = async (id, active) => {
    try {
      const supplierToUpdate = suppliers.value.find(s => s.id === id)
      if (supplierToUpdate) {
        await api.patch(`/suppliers/${id}/`, { active })
        toast.add({
          severity: 'success',
          summary: 'Succès',
          detail: `Fournisseur ${active ? 'activé' : 'désactivé'}`,
          life: 3000
        })
      }
    } catch (error) {
      console.error('Error toggling supplier active status:', error)
      // Revert UI change
      const index = suppliers.value.findIndex(s => s.id === id)
      if (index !== -1) {
        suppliers.value[index].active = !active
      }
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Échec de la mise à jour du statut',
        life: 3000
      })
    }
  }
  
  // Open dialog to create new supplier
  const openNewSupplierDialog = () => {
    supplier.value = { ...emptySupplier }
    editMode.value = false
    supplierDialog.value = true
  }
  
  // Open dialog to edit supplier
  const editSupplier = (data) => {
    supplier.value = { ...data }
    editMode.value = true
    supplierDialog.value = true
  }
  
  // Close supplier dialog
  const closeSupplierDialog = () => {
    supplierDialog.value = false
  }
  
  // Save supplier (create or update)
  const saveSupplier = async (supplierData) => {
  try {
    const response = await api.post('/suppliers/create/', supplierData)
    // Add new supplier to the list
    suppliers.value.push(response.data)
    toast.add({
      severity: 'success',
      summary: 'Succès',
      detail: 'Fournisseur créé',
      life: 3000
    })
    
    supplierDialog.value = false
  } catch (error) {
    console.error('Error creating supplier:', error)
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Échec de la création',
      life: 3000
    })
  }
}
  // Confirm supplier deletion
  const confirmDeleteSupplier = (data) => {
    supplier.value = data
    deleteSupplierDialog.value = true
  }
  
  // Delete supplier
  const deleteSupplier = async () => {
    try {
      await api.delete(`/suppliers/${supplier.value.id}/`)
      
      // Remove supplier from the list
      suppliers.value = suppliers.value.filter(s => s.id !== supplier.value.id)
      
      deleteSupplierDialog.value = false
      
      toast.add({
        severity: 'success',
        summary: 'Succès',
        detail: 'Fournisseur supprimé',
        life: 3000
      })
      
    } catch (error) {
      console.error('Error deleting supplier:', error)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Échec de la suppression',
        life: 3000
      })
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .energy-dashboard {
    .p-datatable {
      .p-datatable-header {
        background-color: var(--surface-section);
        border: 1px solid var(--surface-border);
        border-width: 1px 1px 0 1px;
        padding: 1rem;
        font-weight: 600;
      }
      
      .p-datatable-thead > tr > th {
        background-color: var(--surface-section);
        color: var(--surface-900);
        font-weight: 600;
      }
    }
  }
  </style>